import { ref, readonly } from 'vue';
import { log } from '@/lib/log';

// Imperative confirm dialog. One <ConfirmDialogHost /> mounts at the app
// shell; any component calls `useConfirm().confirm({...})` and awaits a
// boolean. Module-level state — never window.__nodecast* (per learnings).
//
// Only one prompt can be open at a time; queued requests resolve in FIFO
// order. Cancelling the topmost via Esc / cancel button rejects only that
// request — the next queued prompt then opens.

export interface ConfirmOptions {
    title?: string;
    body: string;
    /**
     * Optional HTML body. When set, ConfirmDialog renders it via v-html in
     * place of the plain `body` text — used by callers that need basic
     * formatting (`<strong>`, `<br>`, etc.) inside an imperative confirm.
     * The caller is responsible for the safety of the HTML it passes
     * (Trusted-String discipline) — the dialog does not sanitise.
     */
    bodyHtml?: string;
    /** Confirm button label. Defaults to 'OK' (or 'Delete' for danger). */
    confirmLabel?: string;
    /** Cancel button label. Defaults to 'Cancel'. */
    cancelLabel?: string;
    /** Tints the confirm button red and focuses Cancel by default. */
    danger?: boolean;
}

export interface ConfirmRequest {
    id: number;
    opts: ConfirmOptions;
    resolve: (ok: boolean) => void;
}

const queue = ref<ConfirmRequest[]>([]);
let nextId = 1;

function confirm(opts: ConfirmOptions): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
        const req: ConfirmRequest = { id: nextId++, opts, resolve };
        queue.value = [...queue.value, req];
        log.debug('confirm.requested', { id: req.id, danger: !!opts.danger, title: opts.title ?? null });
    });
}

function settle(id: number, ok: boolean) {
    const req = queue.value.find((r) => r.id === id);
    if (!req) return;
    queue.value = queue.value.filter((r) => r.id !== id);
    log.debug('confirm.settled', { id, ok });
    req.resolve(ok);
}

function clear() {
    // Used by tests; resolves all pending as cancelled.
    for (const r of queue.value) r.resolve(false);
    queue.value = [];
}

export function useConfirm() {
    return {
        queue: readonly(queue),
        confirm,
        settle,
        clear,
    };
}
